import type { Firestore } from 'firebase-admin/firestore'
import type { User, LeaderboardEntry, Achievement, BadgeId, Badge } from '@my-roadtrip/types'
import type { RedisClient } from '../../core/redis/client.js'
import { safeJsonParse } from '../../core/database/redis'
import { GamificationRepository } from './gamification.repository'
import { emitBadgeUnlocked, emitLevelUp } from '../../core/events/eventEmitter.js'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('GamificationService')

export class GamificationService {
  private readonly repository: GamificationRepository

  constructor(
    private readonly firestore: Firestore,
    private readonly redis: RedisClient
  ) {
    this.repository = new GamificationRepository(firestore)
  }

  async getUserProfile(userId: string): Promise<User> {
    logger.debug('Fetching user profile', { userId })
    
    // Check cache
    const cacheKey = `profile:${userId}`
    const cached = await this.redis.get(cacheKey)
    const parsed = safeJsonParse<User>(cached, cacheKey)
    if (parsed) {
      logger.debug('Cache hit for user profile', { userId })
      return parsed
    }

    logger.info('Cache miss for user profile, fetching from DB', { userId })
    // Get from DB
    let profile = await this.repository.getUserProfile(userId)
    
    // If profile doesn't exist, create a default one
    if (!profile) {
      logger.info('User profile not found, creating default profile', { userId })
      profile = await this.createDefaultProfile(userId)
      logger.info('Default profile created', { userId })
    }

    // Cache for 5 min
    await this.redis.setex(`profile:${userId}`, 300, JSON.stringify(profile))
    logger.debug('User profile cached', { userId, ttl: 300 })

    return profile
  }

  private async createDefaultProfile(userId: string): Promise<User> {
    logger.info('Creating default profile', { userId })
    
    // Get user info from auth (best effort)
    const userDoc = await this.firestore.collection('users').doc(userId).get()
    const userData = userDoc.data() || {}
    logger.debug('User data retrieved from Firestore', { userId, hasData: userDoc.exists })
    
    const defaultProfile: User = {
      id: userId,
      email: userData.email || null,
      displayName: userData.displayName || userData.email || 'User',
      photoURL: userData.photoURL || null,
      picture: userData.photoURL || null,
      roles: ['user'],
      status: 'active',
      plan: { tier: 'free', name: 'Free' },
      quota: { current: { plansGenerated: 0, aiRequests: 0 }, limits: { plansPerMonth: 5, aiRequestsPerDay: 10 } },
      gamification: {
        points: 0,
        level: 1,
        badges: [],
        stats: {
          plansCreated: 0,
          stagesCreated: 0,
          totalDistance: 0,
          totalDuration: 0,
          countriesVisited: 0,
          citiesVisited: 0,
          sharedPlans: 0,
          collaborations: 0
        },
        fuel: 100, // Default fuel (full)
        maxFuel: 100, // Default max fuel
        lastFuelRegeneration: new Date() // Initialize to now
      },
      preferences: {
        userId,
        travel: { defaultTransport: 'car', defaultPace: 'normal', avoidTolls: false, avoidHighways: false, avoidFerries: false },
        display: { theme: 'auto', language: 'fr', units: 'metric' },
        notifications: { push: true, email: true, emailFrequency: 'daily', types: { tripReminders: true, socialActivity: true, achievements: true, planUpdates: true } },
        privacy: {
          profilePublic: false,
          showAvatar: true,
          showBio: true,
          showLocation: false,
          showStats: true,
          allowPublicTrips: true,
          allowLocationTracking: true,
          allowAnalytics: true,
          shareLiveWithFriends: true,
          shareLiveWithClans: true
        },
        security: { twoFactorEnabled: false, twoFactorMethod: null }
      },
      languages: [],
      interests: [],
      planCount: 0,
      shareCount: 0,
      flagsCount: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      lastLoginAt: new Date()
    }

    // Save to Firestore
    await this.firestore.collection('users').doc(userId).set(defaultProfile, { merge: true })
    logger.info('Default profile saved to Firestore', { userId })

    return defaultProfile
  }

  async getLeaderboard(period: 'week' | 'month' | 'all', lastDocId?: string, limit = 50): Promise<LeaderboardEntry[]> {
    logger.debug('Fetching leaderboard', { period, limit, lastDocId })
    
    const cacheKey = `leaderboard:${period}:${lastDocId || 'first'}:${limit}`
    const cached = await this.redis.get(cacheKey)
    const parsed = safeJsonParse<LeaderboardEntry[]>(cached, cacheKey)
    if (parsed) {
      logger.debug('Cache hit for leaderboard', { period, count: parsed.length })
      return parsed
    }

    logger.info('Fetching leaderboard from repository', { period, limit })
    const leaderboard = await this.repository.getLeaderboard(period, limit, lastDocId)
    logger.info('Leaderboard fetched', { period, count: leaderboard.length })

    // Cache for 10 min
    await this.redis.setex(cacheKey, 600, JSON.stringify(leaderboard))

    return leaderboard
  }

  async getRecentAchievements(userId: string): Promise<Achievement[]> {
    return this.repository.getRecentAchievements(userId, 10)
  }

  async claimBadge(userId: string, badgeId: BadgeId): Promise<void> {
    logger.debug('Attempting to claim badge', { userId, badgeId })
    const canClaim = await this.canClaimBadge(userId, badgeId)
    if (!canClaim) throw new Error('Badge requirements not met')

    await this.repository.unlockBadge(userId, badgeId)
    logger.info('Badge unlocked', { userId, badgeId })
    
    // Emit badge unlocked event
    emitBadgeUnlocked({
      userId,
      badgeId,
      badgeName: this.getBadgeName(badgeId),
      timestamp: Date.now(),
    })
    
    // Invalidate cache
    await this.redis.del(`profile:${userId}`)
  }

  async awardPoints(userId: string, action: string, amount: number): Promise<void> {
    logger.debug('Awarding points', { userId, action, amount })
    await this.repository.addPoints(userId, action, amount)
    logger.info('Points awarded', { userId, action, amount })
    
    // Check level up
    const profile = await this.repository.getUserProfile(userId)
    if (!profile?.gamification) return

    const newLevel = Math.floor(profile.gamification.points / 100) + 1
    if (newLevel > profile.gamification.level) {
      await this.repository.updateLevel(userId, newLevel)
      logger.info('User leveled up', { userId, newLevel })
      
      // Emit level up event
      emitLevelUp({
        userId,
        newLevel,
        timestamp: Date.now(),
      })
      
      // Create achievement
      await this.repository.createAchievement({
        userId,
        type: 'level',
        title: `Level ${newLevel} Reached!`,
        description: `You've reached level ${newLevel}`,
        level: newLevel
      })
    }

    // Invalidate cache
    await this.redis.del(`profile:${userId}`)
  }

  private async canClaimBadge(userId: string, badgeId: BadgeId): Promise<boolean> {
    const profile = await this.repository.getUserProfile(userId)
    if (!profile?.gamification) return false

    // Check if already unlocked
    if (profile.gamification.badges.some((b: Badge) => b.id === badgeId)) return false

    // Check requirements
    const stats = profile.gamification.stats
    switch (badgeId) {
      case 'first_plan':
        return stats.plansCreated >= 1
      case 'road_warrior':
        return stats.totalDistance >= 1000000 // 1000km
      case 'explorer':
        return stats.countriesVisited >= 10
      case 'socialite':
        return stats.sharedPlans >= 5
      case 'completionist':
        return stats.plansCreated >= 10
      default:
        return false
    }
  }

  private getBadgeName(badgeId: BadgeId): string {
    const names: Record<BadgeId, string> = {
      first_plan: 'First Plan',
      road_warrior: 'Road Warrior',
      explorer: 'Explorer',
      socialite: 'Socialite',
      completionist: 'Completionist',
      collaborator: 'Collaborator',
      early_bird: 'Early Bird',
      globe_trotter: 'Globe Trotter',
    }
    return names[badgeId] || badgeId
  }

  // ========================================
  // FUEL SYSTEM
  // ========================================

  /**
   * Get current fuel for user (with auto-regeneration check)
   */
  async getFuel(userId: string): Promise<{ fuel: number; maxFuel: number; lastRegeneration: Date }> {
    const profile = await this.getUserProfile(userId)
    
    if (!profile.gamification) {
      throw new Error('Gamification profile not found')
    }

    const fuel = profile.gamification.fuel ?? 100
    const maxFuel = profile.gamification.maxFuel ?? 100
    const lastRegeneration = profile.gamification.lastFuelRegeneration ?? new Date()

    // Check if daily regeneration is needed
    const now = new Date()
    const lastRegen = new Date(lastRegeneration)
    const hoursSinceRegen = (now.getTime() - lastRegen.getTime()) / (1000 * 60 * 60)

    // If more than 24h since last regen, do daily reset
    if (hoursSinceRegen >= 24) {
      logger.info('Daily fuel regeneration triggered', { userId })
      await this.regenerateFuel(userId)
      return { fuel: maxFuel, maxFuel, lastRegeneration: now }
    }

    logger.debug('Fuel snapshot returned', { userId, fuel, maxFuel })
    return { fuel, maxFuel, lastRegeneration: lastRegen }
  }

  /**
   * Spend fuel (deduct from current fuel)
   * @throws Error if insufficient fuel
   */
  async spendFuel(userId: string, amount: number, reason: string): Promise<number> {
    if (amount <= 0) {
      throw new Error('Fuel amount must be positive')
    }

    const { fuel: currentFuel } = await this.getFuel(userId)

    if (currentFuel < amount) {
      logger.warn('Fuel spend blocked: insufficient fuel', { userId, requested: amount, available: currentFuel, reason })
      throw new Error('Insufficient fuel')
    }

    const newFuel = currentFuel - amount

    // Update in Firestore
    await this.firestore.collection('users').doc(userId).update({
      'gamification.fuel': newFuel,
      updatedAt: new Date()
    })

    // Invalidate cache
    await this.redis.del(`profile:${userId}`)

    logger.info('Fuel spent', { userId, amount, reason, remaining: newFuel })

    return newFuel
  }

  /**
   * Regenerate fuel to max (daily reset)
   */
  async regenerateFuel(userId: string): Promise<void> {
    const profile = await this.getUserProfile(userId)
    const maxFuel = profile.gamification?.maxFuel ?? 100

    await this.firestore.collection('users').doc(userId).update({
      'gamification.fuel': maxFuel,
      'gamification.lastFuelRegeneration': new Date(),
      updatedAt: new Date()
    })

    // Invalidate cache
    await this.redis.del(`profile:${userId}`)

    logger.info('Fuel regenerated to max', { userId, maxFuel })
  }

  /**
   * Check if user has enough fuel
   */
  async hasFuel(userId: string, amount: number): Promise<boolean> {
    const { fuel } = await this.getFuel(userId)
    return fuel >= amount
  }
}
